/**
 * Геометрия клиентского узла с гидрострелкой: шаг боковых на стрелке и межосевое подача/обратка гребёнок.
 * Используется в CollectorWithSeparatorCharacteristicsCalculator и для согласования
 * распределительного коллектора (клиент, мощность свыше таблицы) с тем же расчётом, что у стрелки.
 */

import { getConnectionWallThickness, getTransitionPipeDiameter } from './endConnectionVelocitySizing';

/** Целевая скорость в корпусе стрелки для клиента при мощности свыше таблицы, м/с */
export const CLIENT_OVER_TABLE_SEP_CORPUS_TARGET_VELOCITY_MS = 0.21;

/** Каталог круглого корпуса гидрострелки (как в CollectorWithSeparatorCharacteristicsCalculator). */
export const ALL_SEPARATOR_DIAMETERS = [
  { outerDiameter: 42.4, wallThickness: 2 },
  { outerDiameter: 42.4, wallThickness: 3 },
  { outerDiameter: 48.3, wallThickness: 2 },
  { outerDiameter: 48.3, wallThickness: 3 },
  { outerDiameter: 60.3, wallThickness: 2 },
  { outerDiameter: 60.3, wallThickness: 3 },
  { outerDiameter: 76.1, wallThickness: 2 },
  { outerDiameter: 76.1, wallThickness: 3 },
  { outerDiameter: 88.9, wallThickness: 2 },
  { outerDiameter: 88.9, wallThickness: 3 },
  { outerDiameter: 101.6, wallThickness: 2 },
  { outerDiameter: 114.3, wallThickness: 2 },
  { outerDiameter: 114.3, wallThickness: 3 },
  { outerDiameter: 129, wallThickness: 2 },
  { outerDiameter: 129, wallThickness: 3 },
  { outerDiameter: 139.7, wallThickness: 2 },
  { outerDiameter: 139.7, wallThickness: 3 },
  { outerDiameter: 159, wallThickness: 2 },
  { outerDiameter: 159, wallThickness: 3 },
  { outerDiameter: 168.3, wallThickness: 3 },
  { outerDiameter: 219.1, wallThickness: 3 },
  { outerDiameter: 273, wallThickness: 3 },
  { outerDiameter: 323.9, wallThickness: 3 },
  { outerDiameter: 355.6, wallThickness: 3 },
  { outerDiameter: 406.4, wallThickness: 3 },
  { outerDiameter: 508, wallThickness: 3 },
  { outerDiameter: 609.6, wallThickness: 3 },
];

export const ALL_SEPARATOR_SQUARE_PROFILES = [
  { profile: '40x40', wallThickness: 2 },
  { profile: '50x50', wallThickness: 2 },
  { profile: '60x60', wallThickness: 2 },
  { profile: '80x80', wallThickness: 2 },
  { profile: '100x100', wallThickness: 3 },
  { profile: '150x150', wallThickness: 3 },
];

function calculateVelocityRoundSeparator(flowRateM3h, outerDiameter, wallThickness = 2) {
  const flowRateM3s = flowRateM3h / 3600;
  const innerDiameter = outerDiameter - 2 * wallThickness;
  const area = Math.PI * Math.pow(innerDiameter / 1000 / 2, 2);
  return area > 0 ? flowRateM3s / area : 0;
}

export function roundUp5mm(v) {
  return Math.ceil(v / 5) * 5;
}

/**
 * Межосевое подача/обратка гребёнок от шага боковых на стрелке:
 * (шаг − 50 мм), вниз до кратного 50 мм, не ниже 125 мм.
 */
export function collectorInteraxisFromSeparatorConnectionSpacingMm(sepConnSpacingMm) {
  const raw = sepConnSpacingMm - 50;
  return Math.max(125, Math.floor(raw / 50) * 50);
}

/** Шаг между центрами боковых отверстий на стрелке (мм), как в calculateParameters комбинированного узла. */
export function separatorLateralStepMmFromConnAndSepInner(connInnerD, sepInnerD) {
  const a = Number(connInnerD);
  const b = Number(sepInnerD);
  if (!Number.isFinite(a) || !Number.isFinite(b)) return null;
  return roundUp5mm(Math.max(3 * a, 2 * b));
}

/**
 * Оптимальная строка каталога корпуса стрелки: скорость в корпусе ближе к targetCorpusVelocityMs,
 * допустимый диапазон корпуса 0,09–0,25 м/с (как filteredSepDiametersBase в CWS).
 */
export function pickOptimalSeparatorCorpusRowForClientFlow(
  actualFlowRateM3h,
  isSquare,
  targetCorpusVelocityMs,
) {
  const TARGET_V = targetCorpusVelocityMs;
  if (!Number.isFinite(actualFlowRateM3h) || actualFlowRateM3h <= 0) return null;
  if (!Number.isFinite(TARGET_V)) return null;

  if (isSquare) {
    const list = ALL_SEPARATOR_SQUARE_PROFILES.filter(({ profile, wallThickness }) => {
      const side = parseInt(profile.split('x')[0], 10);
      const inner = side - 2 * wallThickness;
      const area = (inner / 1000) * (inner / 1000);
      const v = area > 0 ? (actualFlowRateM3h / 3600) / area : 0;
      return v >= 0.09 && v <= 0.25;
    });
    if (!list.length) return null;
    const grouped = {};
    for (const row of list) {
      if (!grouped[row.profile] || row.wallThickness < grouped[row.profile].wallThickness) {
        grouped[row.profile] = row;
      }
    }
    const candidates = Object.values(grouped);
    let best = candidates[0];
    let bestDiff = Infinity;
    for (const row of candidates) {
      const side = parseInt(row.profile.split('x')[0], 10);
      const inner = side - 2 * row.wallThickness;
      const area = (inner / 1000) * (inner / 1000);
      const v = area > 0 ? (actualFlowRateM3h / 3600) / area : 0;
      const diff = Math.abs(v - TARGET_V);
      if (diff < bestDiff) {
        bestDiff = diff;
        best = row;
      }
    }
    return best;
  }

  const list = ALL_SEPARATOR_DIAMETERS.filter(({ outerDiameter, wallThickness }) => {
    const v = calculateVelocityRoundSeparator(actualFlowRateM3h, outerDiameter, wallThickness);
    return v >= 0.09 && v <= 0.25;
  });
  if (!list.length) return null;
  const grouped = {};
  for (const row of list) {
    if (!grouped[row.outerDiameter] || row.wallThickness < grouped[row.outerDiameter].wallThickness) {
      grouped[row.outerDiameter] = row;
    }
  }
  const candidates = Object.values(grouped);
  let best = candidates[0];
  let bestDiff = Infinity;
  for (const row of candidates) {
    const v = calculateVelocityRoundSeparator(actualFlowRateM3h, row.outerDiameter, row.wallThickness);
    const diff = Math.abs(v - TARGET_V);
    if (diff < bestDiff) {
      bestDiff = diff;
      best = row;
    }
  }
  return best;
}

export function separatorCorpusInnerD_mmFromRow(row, isSquare) {
  if (!row) return null;
  if (isSquare) {
    const side = parseInt(String(row.profile).split('x')[0], 10);
    const wt = row.wallThickness;
    if (!Number.isFinite(side) || !Number.isFinite(wt)) return null;
    return side - 2 * wt;
  }
  const od = row.outerDiameter;
  const wt = row.wallThickness;
  if (!Number.isFinite(od) || !Number.isFinite(wt)) return null;
  return od - 2 * wt;
}

/** Внутренний ⌀ трубы торцевого подключения источника (как в расчёте шага на стрелке). */
export function endConnectionStubInnerD_mm(connectionType, sizeKey) {
  if (sizeKey == null) return null;
  const wt = getConnectionWallThickness(connectionType);
  const outer = getTransitionPipeDiameter(connectionType, sizeKey);
  if (!Number.isFinite(Number(outer))) return null;
  return Number(outer) - 2 * wt;
}

/**
 * Межосевое между подающим и обратным коллектором (мм) для клиента с мощностью свыше таблицы:
 * как у обычного гидроразделителя в CharacteristicsCalculator — шаг max(3·dпатрубка, 2·dкорпуса),
 * округление вверх до 5 мм. Без правила «−50 мм / кратно 50» — оно только для комбинированного узла.
 */
export function computeDistributionClientOverTablePipeSpacingFromSeparatorGeometryMm({
  actualFlowRateM3h,
  isSquare,
  endConnectionType,
  endConnectionSizeKey,
}) {
  const sepRow = pickOptimalSeparatorCorpusRowForClientFlow(
    actualFlowRateM3h,
    isSquare,
    CLIENT_OVER_TABLE_SEP_CORPUS_TARGET_VELOCITY_MS,
  );
  const sepInner = separatorCorpusInnerD_mmFromRow(sepRow, isSquare);
  const connInner = endConnectionStubInnerD_mm(endConnectionType, endConnectionSizeKey);
  const lateral = separatorLateralStepMmFromConnAndSepInner(connInner, sepInner);
  return lateral;
}
